import React, { useState, useContext } from 'react';
import {
  AppBar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Toolbar,
  Typography,
  Snackbar,
  Alert
} from "@mui/material";
import { ArrowBack, Edit, Delete } from '@mui/icons-material';
import { RoutingContext } from "../../routing";
import { SheetContext } from "../../context";
import './Profile.scss';

function AccountsScreen() {
  const { navigateToProfile } = useContext(RoutingContext);
  const { accounts, addAccount, updateAccount, deleteAccount } = useContext(SheetContext);
  
  // State for dialogs
  const [saving, setSaving] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentAccount, setCurrentAccount] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  
  // State for snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handlers for Add Account
  const handleOpenAddDialog = () => {
    setNewAccountName('');
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddAccount = async () => {
    if (!newAccountName.trim()) return;
    
    try {
      setSaving(true);
      await addAccount(newAccountName.trim());
      setSnackbar({
        open: true,
        message: 'Account added successfully',
        severity: 'success'
      });
      handleCloseAddDialog();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error adding account: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handlers for Edit Account
  const handleOpenEditDialog = (account) => {
    setCurrentAccount(account);
    setNewAccountName(account);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleUpdateAccount = async () => {
    if (!newAccountName.trim() || newAccountName.trim() === currentAccount) {
      handleCloseEditDialog();
      return;
    }
    
    try {
      setSaving(true);
      await updateAccount(currentAccount, newAccountName.trim());
      setSnackbar({
        open: true,
        message: 'Account updated successfully',
        severity: 'success'
      });
      handleCloseEditDialog();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error updating account: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handlers for Delete Account
  const handleOpenDeleteDialog = (account) => {
    setCurrentAccount(account);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteAccount = async () => {
    try {
      setSaving(true);
      await deleteAccount(currentAccount);
      setSnackbar({
        open: true,
        message: 'Account deleted successfully',
        severity: 'success'
      });
      handleCloseDeleteDialog();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error deleting account: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handler for Snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <>
      <AppBar color="transparent" position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={navigateToProfile}
            aria-label="back"
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Accounts
          </Typography>
          <Button></Button>
        </Toolbar>
      </AppBar>

      <div className='screen-content accounts-screen'>
        <List>
          <Divider />
          {accounts.map((account) => (
            <React.Fragment key={account}>
              <ListItem>
                <ListItemText primary={account} />
                <ListItemSecondaryAction>
                  {account !== 'Cash' && (
                    <>
                      <IconButton 
                        edge="end" 
                        aria-label="edit"
                        onClick={() => handleOpenEditDialog(account)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleOpenDeleteDialog(account)}
                      >
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>

        <Button
          color="primary"
          variant="contained"
          onClick={handleOpenAddDialog}
        >
          Add Account
        </Button>
      </div>

      {/* Add Account Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog}>
        <DialogTitle>Add New Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter the name of the new account:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Account Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddAccount} color="primary" disabled={saving}>Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update the name of the account:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Account Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateAccount} color="primary" disabled={saving}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the account "{currentAccount}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteAccount} color="error" disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AccountsScreen;